import React, { useEffect, useState } from "react";
import { User, Save, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { title: "Mon profil", subtitle: "Gérez vos informations personnelles et votre mot de passe.",
    info: "Informations personnelles", name: "Nom complet", email: "Email", emailNote: "L'email ne peut pas être modifié.",
    phone: "Téléphone", whatsapp: "WhatsApp", country: "Pays de résidence", countryP: "Sélectionnez votre pays",
    save: "Enregistrer", saving: "Enregistrement…", saved: "Profil mis à jour !",
    security: "Sécurité", current: "Mot de passe actuel", newp: "Nouveau mot de passe", confirm: "Confirmer le nouveau mot de passe",
    change: "Changer le mot de passe", changing: "Modification…", changed: "Mot de passe modifié !",
    mismatch: "Les mots de passe ne correspondent pas.", minlen: "Le mot de passe doit contenir au moins 6 caractères." },
  en: { title: "My profile", subtitle: "Manage your personal information and your password.",
    info: "Personal information", name: "Full name", email: "Email", emailNote: "Email cannot be changed.",
    phone: "Phone", whatsapp: "WhatsApp", country: "Country of residence", countryP: "Select your country",
    save: "Save", saving: "Saving…", saved: "Profile updated!",
    security: "Security", current: "Current password", newp: "New password", confirm: "Confirm new password",
    change: "Change password", changing: "Changing…", changed: "Password changed!",
    mismatch: "Passwords do not match.", minlen: "Password must be at least 6 characters." },
};

const COUNTRIES = {
  fr: [
    { v: "France", l: "France" }, { v: "Belgique", l: "Belgique" }, { v: "Suisse", l: "Suisse" },
    { v: "Luxembourg", l: "Luxembourg" }, { v: "Autre", l: "Autre pays" },
  ],
  en: [
    { v: "France", l: "France" }, { v: "Belgique", l: "Belgium" }, { v: "Suisse", l: "Switzerland" },
    { v: "Luxembourg", l: "Luxembourg" }, { v: "Autre", l: "Other country" },
  ],
};

export default function Profil() {
  const { lang } = useLang();
  const { user, setUser } = useAuth();
  const c = C[lang];
  const [form, setForm] = useState({ name: "", email: "", phone: "", whatsapp: "", country: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setForm({
      name: r.data.name || "", email: r.data.email || "", phone: r.data.phone || "",
      whatsapp: r.data.whatsapp || "", country: r.data.country || "",
    })).catch(() => {});
  }, []);

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const { data } = await api.patch("/me", { name: form.name, phone: form.phone, whatsapp: form.whatsapp, country: form.country });
      setUser((u) => ({ ...(u || {}), name: data.name, phone: data.phone, whatsapp: data.whatsapp, country: data.country }));
      toast.success(c.saved);
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setSavingInfo(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) { toast.error(c.minlen); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error(c.mismatch); return; }
    setSavingPwd(true);
    try {
      await api.post("/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(c.changed);
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setSavingPwd(false); }
  };

  return (
    <div data-testid="profil-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.title}</h1>
      <p className="text-muted-foreground mt-2">{c.subtitle}</p>

      <form onSubmit={saveInfo} className="mt-8 bg-white border border-border p-6 space-y-5 max-w-2xl">
        <h2 className="flex items-center gap-2 font-heading text-2xl text-navy"><User size={22} className="text-gold" /> {c.info}</h2>
        <div>
          <Label className="text-navy">{c.name}</Label>
          <Input data-testid="profil-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" />
        </div>
        <div>
          <Label className="text-navy">{c.email}</Label>
          <Input data-testid="profil-email" value={form.email} disabled className="mt-2 rounded-none bg-[#F3F4F6]" />
          <p className="text-xs text-muted-foreground mt-1">{c.emailNote}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-navy">{c.phone}</Label>
            <Input data-testid="profil-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-none" placeholder="+33 6 12 34 56 78" />
          </div>
          <div>
            <Label className="text-navy">{c.whatsapp}</Label>
            <Input data-testid="profil-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-2 rounded-none" placeholder="+33 7 49 85 75 87" />
          </div>
        </div>
        <div>
          <Label className="text-navy">{c.country}</Label>
          <select data-testid="profil-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="mt-2 w-full h-10 border border-input bg-background px-3 py-2 text-sm rounded-none outline-none focus:border-gold transition-colors">
            <option value="">{c.countryP}</option>
            {COUNTRIES[lang].map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
          </select>
        </div>
        <button type="submit" data-testid="profil-save" disabled={savingInfo} className="bg-navy hover:bg-navy-light text-white px-6 py-3 font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
          {savingInfo ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {savingInfo ? c.saving : c.save}
        </button>
      </form>

      <form onSubmit={changePwd} className="mt-8 bg-white border border-border p-6 space-y-5 max-w-2xl">
        <h2 className="flex items-center gap-2 font-heading text-2xl text-navy"><Lock size={22} className="text-gold" /> {c.security}</h2>
        <div>
          <Label className="text-navy">{c.current}</Label>
          <Input type="password" data-testid="profil-current-pwd" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="mt-2 rounded-none" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-navy">{c.newp}</Label>
            <Input type="password" data-testid="profil-new-pwd" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="mt-2 rounded-none" />
          </div>
          <div>
            <Label className="text-navy">{c.confirm}</Label>
            <Input type="password" data-testid="profil-confirm-pwd" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-2 rounded-none" />
          </div>
        </div>
        <button type="submit" data-testid="profil-change-pwd" disabled={savingPwd || !pwd.current_password || !pwd.new_password} className="bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
          {savingPwd ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} {savingPwd ? c.changing : c.change}
        </button>
      </form>
    </div>
  );
}
